import { ReactElement } from 'react';
import { Alert, Button, Table, Upload } from 'antd';
import type { TableColumnsType } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useGStaffUpload } from '../hooks/useGStaffUpload';
import type { WorkHistoryRowError } from '../api/gStaffMutations';
import { SectionCard, CardTitle, UploadHint, UploadFooter, UploadPreviewBlock, UploadPreviewSummary } from './styles';

const errorRowColumns: TableColumnsType<WorkHistoryRowError> = [
  { title: '行', dataIndex: 'rowNumber', width: 72 },
  { title: 'スタッフ名カナ', dataIndex: 'nameKana' },
  { title: '理由', dataIndex: 'reason' },
];

const GStaffUpload = (): ReactElement => {
  const { fileList, preview, validating, uploading, changeFile, confirmUpload } = useGStaffUpload();

  return (
    <SectionCard>
      <CardTitle>Gスタッフ稼働歴</CardTitle>
      <UploadHint>
        Gスタッフからエクスポートした稼働歴ファイル（Excel
        .xlsx）をアップロードして最新版に更新します。ファイルを選択すると検証結果が表示され、「アップロードして反映」で確定します。
      </UploadHint>
      <Upload
        accept='.xlsx'
        maxCount={1}
        beforeUpload={() => false}
        fileList={fileList}
        disabled={validating || uploading}
        onChange={info => {
          void changeFile(info.fileList);
        }}
      >
        <Button icon={<UploadOutlined />} loading={validating}>
          ファイルを選択
        </Button>
      </Upload>
      {preview !== null && (
        <UploadPreviewBlock>
          <UploadPreviewSummary>
            データ行数 {preview.totalRows}件 / 取込対象 {preview.recordCount}件
            {preview.duplicateCount > 0 && `（重複 ${String(preview.duplicateCount)}件を除外）`}
          </UploadPreviewSummary>
          {preview.errorRowCount > 0 && (
            <>
              <Alert
                type='warning'
                showIcon
                message={`取り込めない行が${String(preview.errorRowCount)}件あります。これらの行は除外して反映されます。${
                  preview.errorRowCount > preview.errorRows.length
                    ? `（一覧は先頭${String(preview.errorRows.length)}件のみ表示）`
                    : ''
                }`}
              />
              <Table
                columns={errorRowColumns}
                dataSource={preview.errorRows}
                rowKey='rowNumber'
                size='small'
                pagination={{ pageSize: 5, hideOnSinglePage: true }}
              />
            </>
          )}
        </UploadPreviewBlock>
      )}
      <UploadFooter>
        <Button
          type='primary'
          onClick={() => {
            void confirmUpload();
          }}
          loading={uploading}
          disabled={preview === null || preview.recordCount === 0 || validating}
        >
          アップロードして反映
        </Button>
      </UploadFooter>
    </SectionCard>
  );
};

export default GStaffUpload;
